import { ChevronLeft, ChevronRight, Maximize, X } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router";

import { findChoice, STYLE_STATUSES } from "../common/constants";
import { StatusBadge } from "../common/components";
import type { Style } from "../types";
import { StylePreview } from "./StylePreview";
import { resolveTokens } from "./tokens";

/** Full-screen, menu-less walkthrough of the library for client meetings. */
export const StylePresent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const translate = useTranslate();
  const { data: styles } = useGetList<Style>("styles", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
  });
  const index = styles?.findIndex((s) => String(s.id) === id) ?? -1;
  const style = index >= 0 ? styles![index] : undefined;

  const go = useCallback(
    (delta: number) => {
      if (!styles?.length) return;
      const next = styles[(index + delta + styles.length) % styles.length];
      navigate(`/styles/${next.id}/present`, { replace: true });
    },
    [styles, index, navigate],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "Escape") navigate(`/styles/${id}/show`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, navigate, id]);

  if (!style) return null;
  const t = resolveTokens(style.tokens);

  return (
    <div
      className="flex min-h-screen flex-col gap-6 p-6 md:p-10"
      style={{ background: t.colors.background, color: t.colors.foreground }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <h1
              className="text-3xl font-semibold md:text-4xl"
              style={{ fontFamily: `"${t.fonts.heading}", system-ui` }}
            >
              {style.name}
            </h1>
            <StatusBadge
              choice={findChoice(STYLE_STATUSES, style.status)}
              className="bg-white/70 text-black"
            />
          </div>
          {style.description && (
            <p className="max-w-3xl opacity-80">{style.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <IconButton
            label="⛶"
            onClick={() => document.documentElement.requestFullscreen?.()}
          >
            <Maximize className="size-5" />
          </IconButton>
          <IconButton
            label={translate("hub.styles.exit_present")}
            onClick={() => navigate(`/styles/${style.id}/show`)}
          >
            <X className="size-5" />
          </IconButton>
        </div>
      </div>
      <div className="flex flex-1 items-center gap-4">
        <IconButton label="←" onClick={() => go(-1)}>
          <ChevronLeft className="size-6" />
        </IconButton>
        <StylePreview tokens={style.tokens} className="flex-1 shadow-2xl" />
        <IconButton label="→" onClick={() => go(1)}>
          <ChevronRight className="size-6" />
        </IconButton>
      </div>
      <p className="text-center text-sm opacity-60">
        {index + 1} / {styles?.length}
      </p>
    </div>
  );
};

const IconButton = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="rounded-full bg-black/10 p-2 transition hover:bg-black/20"
  >
    {children}
  </button>
);

StylePresent.path = "/styles/:id/present";
